import { useCallback, useEffect, useState } from 'react'
import { apiErrorText } from '@/lib/api-error'
import { fieldLabel, fieldWrapper, inputBase } from '@/lib/form-styles'
import { useTerminology } from '@/lib/useTerminology'
import { paste, previewPaste, type LvPasteVorschau } from '../api/projects-phase-d.api'
import { LV_HINWEIS, lvMenge } from './lv-labels'
import type { LvPasteMode } from '@symphenia/shared-types'
import { PASTE_NOTE_LABELS, PASTE_OUTCOME_LABELS } from '../project-labels'
import type { PastePreviewRowView, PositionGroup } from '../types/phase-d.types'
import type { ProjectDetail } from '../types/project.types'

/**
 * AP-35 Detailblatt 2 7.4 — **„Einfügen aus der Zwischenablage".**
 *
 * Das Herzstück ist die Spalte **„wird zu"**: Bevor irgendetwas angelegt wird,
 * sagt der **Server**, was aus jedem Eintrag würde — Position, Gruppe, Langtext
 * oder nichts. Die Vorschau ist derselbe Aufruf mit `dryRun`, nicht eine zweite
 * Rechnung in der Maske: Eine Vorschau, die anders entscheidet als der Vorgang,
 * wäre schlimmer als gar keine.
 *
 * Gelingt das Einfügen, ist die Ablage leer; scheitert es, bleibt sie gefüllt
 * (AP-32 5.7) — der Benutzer verliert nichts, wenn eine Einheit gesperrt ist.
 */
export function PositionPasteDialog({
  projekt,
  gruppen,
  positionen = [],
  aktuellePosition = null,
  onClose,
  onDone,
}: {
  projekt: ProjectDetail
  gruppen: PositionGroup[]
  /** AP-35 J: Ziel für „als Verkaufszeilen in Position …". */
  positionen?: { id: string; number: number; name: string }[]
  aktuellePosition?: string | null
  onClose: () => void
  onDone: (meldung: string) => void
}) {
  const terms = useTerminology()
  const [titelAlsGruppen, setTitelAlsGruppen] = useState(true)
  const [mitOptionalen, setMitOptionalen] = useState(false)
  const [zielgruppe, setZielgruppe] = useState('')
  const [vorschau, setVorschau] = useState<PastePreviewRowView[]>([])
  // AP-35 J (Blatt 12 5.2): nur LV-Positionen in der Ablage → drei Modi, Teilmengen.
  const [lv, setLv] = useState<LvPasteVorschau | null>(null)
  const [modus, setModus] = useState<LvPasteMode | null>(null)
  const [name, setName] = useState('')
  const [ziel, setZiel] = useState(aktuellePosition ?? '')
  const [mengen, setMengen] = useState<Record<string, string>>({})
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  const optionen = useCallback(
    () => ({
      titlesAsGroups: titelAlsGruppen,
      includeOptional: mitOptionalen,
      targetGroupId: zielgruppe || undefined,
    }),
    [titelAlsGruppen, mitOptionalen, zielgruppe],
  )

  useEffect(() => {
    void previewPaste(projekt.id, optionen())
      .then((a) => {
        setVorschau(a.preview ?? [])
        if (a.lv) {
          const v = a.lv
          setLv(v)
          setModus((m) => m ?? v.mode)
          setName((n) => n || v.mergedName)
          setMengen((m) => (Object.keys(m).length ? m : Object.fromEntries(v.lines.map((l) => [l.clipboardItemId, String(l.quantity).replace('.', ',')]))))
        }
        setFehler(null)
      })
      .catch((e) => setFehler(apiErrorText(e, 'Das hat nicht geklappt.')))
  }, [projekt.id, optionen])

  const anzahlPositionen = vorschau.filter((v) => v.outcome === 'POSITION').length

  const lvOptionen = () => {
    const quantities: Record<string, number> = {}
    for (const [id, text] of Object.entries(mengen)) {
      const n = Number(text.replace(/\./g, '').replace(',', '.'))
      if (Number.isFinite(n) && n > 0) quantities[id] = n
    }
    return {
      mode: modus ?? undefined,
      mergedName: modus === 'MERGE' ? name.trim() || undefined : undefined,
      targetPositionId: modus === 'INTO_POSITION' ? ziel || undefined : undefined,
      targetGroupId: zielgruppe || undefined,
      quantities,
      // Aus der Kalkulation: Eventual und Wahl kommen als nicht verrechenbare Zeilen mit, wie aus dem LV.
      ...(lv?.source === 'CALCULATION' ? { includeOptional: true } : {}),
    }
  }

  async function einfuegen() {
    setLaeuft(true)
    setFehler(null)
    try {
      const ergebnis = await paste(projekt.id, lv ? lvOptionen() : optionen())
      const hinweise = ergebnis.hints.length ? ` · ${ergebnis.hints.length} Hinweise` : ''
      onDone(lv ? `${lv.lines.length} ${lv.source === 'CALCULATION' ? 'Kalkulationspositionen' : 'LV-Positionen'} eingefügt${hinweise}` : `${ergebnis.positionIds.length} ${terms.positionPlural} eingefügt${hinweise}`)
    } catch (e) {
      // Die Ablage bleibt gefüllt — der Dialog darf offen bleiben, damit man es
      // nach der Korrektur sofort erneut versuchen kann.
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[85vh] w-full max-w-3xl flex-col gap-4 rounded-lg border border-border bg-background p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-primary">Einfügen aus der Zwischenablage</h2>

        <div className="grid gap-3 sm:grid-cols-3">
          {/* Aus dem LV: keine Titelzeilen, Eventual wird zur nicht verrechenbaren Zeile. */}
          {!lv && (
            <>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={titelAlsGruppen}
                  onChange={(e) => setTitelAlsGruppen(e.target.checked)}
                />
                Titelzeilen als Gruppen
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={mitOptionalen} onChange={(e) => setMitOptionalen(e.target.checked)} />
                Eventual-/Alternativpositionen
              </label>
            </>
          )}
          <div className={fieldWrapper}>
            <label className={fieldLabel} htmlFor="paste-gruppe">
              Zielgruppe
            </label>
            <select
              id="paste-gruppe"
              className={inputBase}
              value={zielgruppe}
              onChange={(e) => setZielgruppe(e.target.value)}
            >
              <option value="">ohne Gruppe</option>
              {gruppen.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.number}. {g.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {lv && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-4 text-sm">
              {(
                [
                  ['ONE_EACH', `je ${lv.source === 'CALCULATION' ? 'Kalkulationsposition' : 'LV-Position'} eine ${terms.position}`],
                  ['MERGE', `zu einer ${terms.position} zusammenfassen`],
                  ['INTO_POSITION', `als Verkaufszeilen in ${terms.position} …`],
                ] as const
              ).map(([m, text]) => (
                <label key={m} className="flex items-center gap-1.5">
                  <input type="radio" name="lv-modus" checked={modus === m} onChange={() => setModus(m)} />
                  {text}
                </label>
              ))}
            </div>
            {modus === 'MERGE' && (
              <div className={fieldWrapper}>
                <label className={fieldLabel} htmlFor="paste-name">
                  Bezeichnung der neuen {terms.position}
                </label>
                <input id="paste-name" className={inputBase} value={name} onChange={(e) => setName(e.target.value)} />
              </div>
            )}
            {modus === 'INTO_POSITION' && (
              <div className={fieldWrapper}>
                <label className={fieldLabel} htmlFor="paste-ziel">
                  {terms.position}
                </label>
                <select id="paste-ziel" className={inputBase} value={ziel} onChange={(e) => setZiel(e.target.value)}>
                  <option value="">wählen …</option>
                  {positionen.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.number}. {p.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        {lv ? (
          <div className="min-h-0 flex-1 overflow-y-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-muted text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">{lv.source === 'CALCULATION' ? 'Pos.' : 'OZ'}</th>
                  <th className="px-3 py-2 text-left">Kurztext</th>
                  <th className="px-3 py-2 text-right">Menge</th>
                  <th className="px-3 py-2 text-right">Rest</th>
                </tr>
              </thead>
              <tbody>
                {lv.lines.map((l) => (
                  <tr key={l.clipboardItemId} className="border-t border-border">
                    <td className="whitespace-nowrap px-3 py-1.5 tabular-nums text-muted-foreground">{l.oz}</td>
                    <td className="px-3 py-1.5">
                      {l.childOfOz && <span className="mr-1 text-xs text-muted-foreground">↳ zu {l.childOfOz}</span>}
                      {l.shortText}
                      {l.notes.map((n) => (
                        <span key={n} className="ml-2 text-xs text-amber-700 dark:text-amber-400">
                          {LV_HINWEIS[n] ?? n}
                        </span>
                      ))}
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <input
                        className={`${inputBase} w-24 text-right`}
                        value={mengen[l.clipboardItemId] ?? ''}
                        onChange={(e) => setMengen((m) => ({ ...m, [l.clipboardItemId]: e.target.value }))}
                        aria-label={`Menge LV ${l.oz}`}
                      />{' '}
                      <span className="text-xs text-muted-foreground">{l.unitCode}</span>
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">{lvMenge(l.restQuantity)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
        <div className="min-h-0 flex-1 overflow-y-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Eintrag</th>
                <th className="px-3 py-2 text-left">wird zu</th>
              </tr>
            </thead>
            <tbody>
              {vorschau.map((zeile) => (
                <tr key={zeile.clipboardItemId} className="border-t border-border">
                  <td className="px-3 py-2">
                    {zeile.outcome === 'SKIPPED' || zeile.outcome === 'LONG_TEXT' ? (
                      <span className="text-muted-foreground">
                        {PASTE_NOTE_LABELS[zeile.label] ?? zeile.label}
                      </span>
                    ) : (
                      zeile.label
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={
                        zeile.outcome === 'SKIPPED' ? 'text-muted-foreground' : 'font-medium text-foreground'
                      }
                    >
                      {PASTE_OUTCOME_LABELS[zeile.outcome]}
                    </span>
                    {zeile.notes.map((n) => (
                      <span key={n} className="ml-2 text-xs text-yellow-700 dark:text-yellow-400">
                        {PASTE_NOTE_LABELS[n] ?? n}
                      </span>
                    ))}
                  </td>
                </tr>
              ))}
              {vorschau.length === 0 && (
                <tr>
                  <td colSpan={2} className="px-3 py-4 text-sm text-muted-foreground">
                    Die Zwischenablage ist leer.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        )}

        {fehler && <p className="text-sm text-destructive">{fehler}</p>}

        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground">
            {lv ? `${lv.lines.length} ${lv.source === 'CALCULATION' ? 'Kalkulationspositionen' : 'LV-Positionen'}` : `${anzahlPositionen} ${terms.positionPlural} · ${vorschau.length - anzahlPositionen} anderes`}
          </span>
          <span className="flex gap-2">
            <button type="button" className="rounded-md border border-border px-3 py-1.5 text-sm" onClick={onClose}>
              Abbrechen
            </button>
            <button
              type="button"
              disabled={laeuft || (lv ? !lv.lines.length || (modus === 'INTO_POSITION' && !ziel) : anzahlPositionen === 0)}
              className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50"
              onClick={einfuegen}
            >
              Einfügen
            </button>
          </span>
        </div>
      </div>
    </div>
  )
}
